import { cn } from '@/lib/utils';
import type { HandoverPhotoKind, RentalHandoverPhoto } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getHandoverPhotoContentUrl,
  listHandoverPhotos,
  uploadHandoverPhoto,
} from '../../../api/rental-handover';

/**
 * FE-D2 — handover-fotó feltöltő (Wave-2 / E-105 Phase 3).
 *
 * Egy közös komponens (kind-prop): HANDOVER → kiadáskori, RETURN → visszavételkori
 * állapot-fotó. Server-proxied multipart (a MinIO-adapternek nincs presign, BE-1).
 * A FE-guard (JPEG/PNG/WebP + 10MB) a BE-guard tükre — early-reject a feltöltés előtt,
 * nem helyettesíti a szerver-validációt (azaz nem biztonsági határ, csak UX-gyorsítás).
 */
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export function HandoverPhotoUploader({
  rentalId,
  kind,
  className,
}: {
  rentalId: string;
  kind: HandoverPhotoKind;
  className?: string;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [photos, setPhotos] = useState<RentalHandoverPhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    const list = await listHandoverPhotos(rentalId, kind);
    setPhotos(list);
  }, [rentalId, kind]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleFile = async (file: File): Promise<void> => {
    setError(null);

    if (!ALLOWED_TYPES.includes(file.type as (typeof ALLOWED_TYPES)[number])) {
      setError(t('rental.handover.errorType'));
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setError(t('rental.handover.errorSize', { max: MAX_SIZE_MB }));
      return;
    }

    setUploading(true);
    try {
      await uploadHandoverPhoto(rentalId, file, kind);
      await refresh();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const uploadLabel =
    kind === 'HANDOVER' ? t('rental.handover.uploadHandover') : t('rental.handover.uploadReturn');

  return (
    <div className={cn('space-y-3', className)} data-testid={`handover-photo-uploader-${kind}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t('rental.handover.title')}</h3>
      </div>

      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent">
        <span>{uploading ? t('rental.handover.uploading') : uploadLabel}</span>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          className="sr-only"
          disabled={uploading}
          onChange={onChange}
          data-testid="handover-photo-input"
        />
      </label>

      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="handover-photo-error">
          {error}
        </p>
      )}

      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="handover-photo-empty">
          {t('rental.handover.empty')}
        </p>
      ) : (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map(photo => (
            <li key={photo.id} data-testid={`handover-photo-item-${photo.id}`}>
              <img
                src={getHandoverPhotoContentUrl(rentalId, photo.id)}
                alt={photo.fileName}
                className="aspect-square w-full rounded-md border object-cover"
                loading="lazy"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
